import type { Client } from "openapi-fetch";
import type { paths } from "./api/schema";

type Proposal =
  paths["/booking/public/proposals/{proposalToken}"]["get"]["responses"][200]["content"]["application/json"];
const escape = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

export async function renderBookingProposal(
  root: HTMLElement,
  api: Client<paths>,
  proposalToken: string,
  locale: "cs" | "en",
  onCatalog?: (catalog: Record<string, string>) => void,
  onProblem?: (problem: unknown) => boolean,
) {
  const cs = locale === "cs";
  let catalog: Record<string, string> = {};
  const label = (key: string, cz: string, en: string) =>
    catalog[key] ?? (cs ? cz : en);
  // A proposal URL is a bearer capability. Never send it as a referrer or index it.
  for (const [name, content] of [
    ["referrer", "no-referrer"],
    ["robots", "noindex,nofollow"],
  ]) {
    const meta =
      document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`) ??
      document.createElement("meta");
    meta.name = name!;
    meta.content = content!;
    document.head.append(meta);
  }
  let timer: number | undefined;
  let busy = false;
  const when = (value: string, zone: string) =>
    new Intl.DateTimeFormat(cs ? "cs-CZ" : "en-GB", {
      dateStyle: "full",
      timeStyle: "short",
      timeZone: zone,
    }).format(new Date(value));
  const messages = (): Record<Proposal["status"], string> => ({
    slot_conflict: label(
      "proposal.slotConflict",
      "Navržený termín již není k dispozici. Původní rezervace zůstává beze změny. Požádejte hostitele o nový návrh.",
      "The proposed time is no longer available. The original reservation is unchanged. Ask the host for a new proposal.",
    ),
    technical_error: label(
      "proposal.technicalError",
      "Změnu se nepodařilo technicky ověřit. Obnovte stav a zkuste to znovu. Pokud potíže trvají, předejte hostiteli ID požadavku.",
      "The change could not be verified due to a technical error. Reload the status and retry. If it persists, give the request ID to the host.",
    ),
    pending: label(
      "proposal.awaitingYourDecision",
      "Čeká na vaše rozhodnutí",
      "Awaiting your decision",
    ),
    accepted: label(
      "proposal.acceptedByYouConfirmingTheChangeInOutlook",
      "Potvrzeno vámi. Ověřujeme změnu v Outlooku…",
      "Accepted by you. Confirming the change in Outlook…",
    ),
    succeeded: label(
      "proposal.theMeetingTimeHasChangedOutlookSendsTheUpdateToAllAttendees",
      "Termín byl změněn. Outlook odešle aktualizaci všem účastníkům.",
      "The meeting time has changed. Outlook sends the update to all attendees.",
    ),
    rejected: label(
      "proposal.youDeclinedTheProposalTheOriginalReservationRemainsValid",
      "Návrh jste odmítli. Původní rezervace zůstává platná.",
      "You declined the proposal. The original reservation remains valid.",
    ),
    withdrawn: label(
      "proposal.theHostWithdrewThisProposalOrTheReservationChanged",
      "Hostitel tento návrh stáhl nebo se rezervace mezitím změnila.",
      "The host withdrew this proposal or the reservation changed.",
    ),
    expired: label(
      "proposal.thisProposalExpiredWithoutChangingTheReservation",
      "Platnost návrhu vypršela. Tímto návrhem se rezervace nezměnila.",
      "This proposal expired without changing the reservation.",
    ),
    failed: label(
      "proposal.outlookCouldNotConfirmTheChangeTheOriginalReservationRemainsValidContactTheHost",
      "Outlook změnu nepotvrdil. Původní rezervace zůstává platná. Kontaktujte hostitele.",
      "Outlook could not confirm the change. The original reservation remains valid. Contact the host.",
    ),
  });
  function errorMessage(problem?: unknown) {
    const value =
      problem && typeof problem === "object"
        ? (problem as { status?: number; type?: string; requestId?: string })
        : {};
    const missingEvent = value.type?.endsWith("booking-calendar-event-missing");
    const message = missingEvent
      ? label(
          "proposal.calendarEventMissing",
          "Chybí propojení s kalendářovou událostí. Původní rezervace zůstává beze změny. Požádejte hostitele o opravu synchronizace.",
          "The calendar event link is missing. The original reservation is unchanged. Ask the host to repair calendar synchronization.",
        )
      : value.status === 409
        ? label(
            "proposal.changed",
            "Návrh nebo dostupnost se změnily. Obnovte stav; můžete ponechat původní termín nebo požádat hostitele o nový návrh.",
            "The proposal or availability changed. Reload its status; you can keep the original time or ask the host for a new proposal.",
          )
        : !value.status || value.status >= 500
          ? messages().technical_error
          : undefined;
    if (message)
      return message + (value.requestId ? " (" + value.requestId + ")" : "");
    return label(
      "proposal.theProposalCouldNotBeVerifiedItMayHaveChangedOrExpiredReloadItsCurrentState",
      "Návrh se nepodařilo ověřit. Mohl se mezitím změnit nebo vypršet. Obnovte jeho stav.",
      "The proposal could not be verified. It may have changed or expired. Reload its current state.",
    );
  }
  function draw(data: Proposal) {
    if (data.catalog) catalog = data.catalog;
    onCatalog?.(catalog);
    root.innerHTML = `<section class="card confirmation compact-confirmation"><p class="eyebrow">${escape(label("proposal.proposedTimeChange", "Návrh změny termínu", "Proposed time change"))}</p><h1>${escape(data.meetingName)}</h1>
      <div class="proposal-times"><section><h2>${escape(label("proposal.originalTime", "Původně", "Original time"))}</h2><p>${escape(when(data.originalStartsAt, data.timezone))}</p><p>${escape(data.originalLocation)}</p></section><section><h2>${escape(label("proposal.proposedTime", "Nově", "Proposed time"))}</h2><p>${escape(when(data.targetStartsAt, data.timezone))}</p><p>${escape(data.targetLocation)}</p></section></div>
      ${data.locationChanged ? `<p><strong>${escape(label("proposal.theMeetingLocationAlsoChanges", "Pozor, mění se také místo setkání.", "The meeting location also changes."))}</strong></p>` : ""}
      ${data.reason ? `<h2>${escape(label("proposal.hostExplanation", "Vysvětlení hostitele", "Host explanation"))}</h2><p class="proposal-reason">${escape(data.reason)}</p>` : ""}
      <p role="status" aria-live="polite">${escape(messages()[data.status])}</p>
      ${data.status === "pending" || data.status === "technical_error" ? `<p>${escape(label("proposal.respondBefore", "Rozhodněte se do", "Respond before"))} ${escape(when(data.expiresAt, data.timezone))}.</p><p>${escape(label("proposal.theOriginalTimeOnlyChangesAfterYouAcceptAdditionalAttendeesDoNotApproveTheProposal", "Původní termín se změní až po vašem potvrzení. Ostatní účastníci návrh neschvalují.", "The original time only changes after you accept. Additional attendees do not approve the proposal."))}</p><div class="management-actions"><button class="button" id="accept-proposal">${escape(label("proposal.acceptNewTime", "Přijmout nový termín", "Accept new time"))}</button><button class="button secondary" id="reject-proposal">${escape(label("proposal.keepOriginalTime", "Ponechat původní termín", "Keep original time"))}</button></div>` : ""}
      ${data.status === "accepted" ? `<div class="operation-progress" role="status"><progress></progress><span>${escape(label("proposal.checkingOutlookYouCanSafelyLeaveThisPage", "Ověřujeme Outlook. Stránku můžete bezpečně opustit.", "Checking Outlook. You can safely leave this page."))}</span></div><button class="button secondary" id="resume-proposal">${escape(label("proposal.checkChange", "Zkontrolovat změnu", "Check change"))}</button>` : ""}
      <p id="proposal-error" role="alert" tabindex="-1"></p><button class="button secondary" id="reload-proposal" hidden>${escape(label("proposal.reloadStatus", "Obnovit stav", "Reload status"))}</button>
    </section>`;
    if (data.errorRequestId) {
      const details = document.createElement("p");
      details.textContent =
        label("proposal.requestId", "ID požadavku", "Request ID") +
        ": " +
        data.errorRequestId;
      root.querySelector("section")?.append(details);
    }
    for (const action of ["accept", "reject"] as const)
      root
        .querySelector(`#${action}-proposal`)
        ?.addEventListener("click", () => void respond(data, action));
    root
      .querySelector("#resume-proposal")
      ?.addEventListener("click", () => void respond(data, "accept"));
    root
      .querySelector("#reload-proposal")
      ?.addEventListener("click", () => void load());
    if (data.status === "accepted")
      timer = window.setTimeout(() => void load(), 2500);
  }
  async function respond(data: Proposal, action: "accept" | "reject") {
    if (busy) return;
    busy = true;
    window.clearTimeout(timer);
    root
      .querySelectorAll<HTMLButtonElement>("button")
      .forEach((button) => (button.disabled = true));
    const status = root.querySelector<HTMLElement>('[role="status"]');
    if (status)
      status.textContent = label(
        "proposal.savingYourDecision",
        "Ukládáme vaše rozhodnutí…",
        "Saving your decision…",
      );
    try {
      const result =
        action === "accept"
          ? await api.POST("/booking/public/proposals/{proposalToken}/accept", {
              params: { path: { proposalToken } },
              body: { revision: data.revision },
            })
          : await api.POST("/booking/public/proposals/{proposalToken}/reject", {
              params: { path: { proposalToken } },
              body: { revision: data.revision },
            });
      if (result.error) throw result.error;
      draw(result.data);
    } catch (problem) {
      if (onProblem?.(problem)) return;
      const error = root.querySelector<HTMLElement>("#proposal-error");
      if (error) {
        error.textContent = errorMessage(problem);
        error.focus();
      }
      if (status) status.textContent = messages()[data.status];
      const keep = root.querySelector<HTMLButtonElement>("#reject-proposal");
      if (keep) keep.disabled = false;
      const retry = root.querySelector<HTMLButtonElement>("#reload-proposal");
      if (retry) {
        retry.hidden = false;
        retry.disabled = false;
      }
    } finally {
      busy = false;
    }
  }
  async function load() {
    window.clearTimeout(timer);
    try {
      const { data, error } = await api.GET(
        "/booking/public/proposals/{proposalToken}",
        { params: { path: { proposalToken }, query: { lang: locale } } },
      );
      if (error) throw error;
      draw(data);
    } catch (problem) {
      if (onProblem?.(problem)) return;
      root.innerHTML = `<section class="card error-card"><h1>${escape(label("proposal.proposalUnavailable", "Návrh není dostupný", "Proposal unavailable"))}</h1><p role="alert">${escape(errorMessage(problem))}</p><button class="button" id="reload-proposal">${escape(label("proposal.reloadStatus", "Obnovit stav", "Reload status"))}</button></section>`;
      root
        .querySelector("#reload-proposal")
        ?.addEventListener("click", () => void load());
    }
  }
  root.innerHTML = `<p role="status">${escape(label("proposal.loadingProposal", "Načítáme návrh…", "Loading proposal…"))}</p>`;
  await load();
}
